import {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

import type {
  IDraggablePanelPosition, IDraggablePanelSize,
} from 'components/DraggablePanel/interfaces/IDraggablePanel';
import type IAiChatPanelProvider from './interfaces/IAiChatPanelProvider';

interface IAiChatPanelContextValue {
  isVisible: boolean,
  position: IDraggablePanelPosition,
  size: IDraggablePanelSize,
  isAttached: boolean,
  togglePanel: () => void,
  closePanel: () => void,
  movePanel: (position: IDraggablePanelPosition) => void,
  resizePanel: (size: IDraggablePanelSize) => void,
  toggleAttached: () => void,
}

const DEFAULT_POSITION: IDraggablePanelPosition = { x: 88, y: 88 };
const DEFAULT_SIZE: IDraggablePanelSize = { width: 420, height: 560 };

const AiChatPanelContext = createContext<IAiChatPanelContextValue>({
  isVisible: false,
  position: DEFAULT_POSITION,
  size: DEFAULT_SIZE,
  isAttached: true,
  togglePanel: () => {},
  closePanel: () => {},
  movePanel: () => {},
  resizePanel: () => {},
  toggleAttached: () => {},
});

/**
 * Owns where the assistant panel sits and whether it is on screen.
 *
 * Hiding the panel leaves it mounted, so the chat keeps its messages and its scroll position
 * between one look and the next. `movePanel` and `resizePanel` are the seam the drag and resize
 * handlers will write through.
 */
function AiChatPanelProvider({ children }: IAiChatPanelProvider) {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [position, setPosition] = useState<IDraggablePanelPosition>(DEFAULT_POSITION);
  const [size, setSize] = useState<IDraggablePanelSize>(DEFAULT_SIZE);
  const [isAttached, setIsAttached] = useState<boolean>(true);

  const togglePanel = useCallback(() => setIsVisible((current) => !current), []);
  const closePanel = useCallback(() => setIsVisible(false), []);
  const toggleAttached = useCallback(() => setIsAttached((current) => !current), []);

  const value = useMemo<IAiChatPanelContextValue>(() => ({
    isVisible,
    position,
    size,
    isAttached,
    togglePanel,
    closePanel,
    movePanel: setPosition,
    resizePanel: setSize,
    toggleAttached,
  }), [closePanel, isAttached, isVisible, position, size, toggleAttached, togglePanel]);

  return (
    <AiChatPanelContext.Provider value={value}>
      {children}
    </AiChatPanelContext.Provider>
  );
}

export const useAiChatPanel = () => useContext(AiChatPanelContext);

export default AiChatPanelProvider;
